import { apiAction, API_SUCCESS, API_ERROR } from "../dux/api";
import { apiCallComplete, apiCallStart } from "../dux/apiStatus";
import { openSnackbar } from "../dux/snackbar";
import {
  getCheckBookingAllowedUrl,
  getHolidayDatesUrl,
  getSpecialScheduleDatesUrl,
  getSpecialScheduleUpdateUrl,
  getCheckDatePresenceUrl,
  getDayMappingsUrl,
  getPricingConfigsUrl,
} from "../helpers/apiEndPoints";
import {
  CHECK_HOLIDAY_BOOKING,
  SET_HOLIDAY_BOOKING_STATUS,
  FETCH_HOLIDAY_DATES,
  SET_HOLIDAY_DATES,
  FETCH_SPECIAL_SCHEDULE_DATES,
  SET_SPECIAL_SCHEDULE_DATES,
  UPDATE_SPECIAL_SCHEDULE,
  CHECK_DATE_PRESENCE,
  SET_DATE_PRESENCE_STATUS,
  FETCH_DAY_MAPPINGS,
  SET_DAY_MAPPINGS,
  FETCH_PRICING_CONFIGS,
  SET_PRICING_CONFIGS,
  fetchSpecialScheduleDates,
} from "../dux/bookNow";

const holidayBookingMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    if (action.type === CHECK_HOLIDAY_BOOKING) {
      const { date, sport, location } = action.payload;

      // Start API call
      next(apiCallStart({ api: "checkHolidayBooking" }));

      // Make API call to check holiday booking status
      next(
        apiAction({
          url: getCheckBookingAllowedUrl(location, sport, date),
          feature: CHECK_HOLIDAY_BOOKING,
          method: "GET",
        })
      );
    }

    if (action.type === `${CHECK_HOLIDAY_BOOKING} ${API_SUCCESS}`) {
      // Set the holiday booking status in the store
      next({
        type: SET_HOLIDAY_BOOKING_STATUS,
        payload: { status: action.payload.response || action.payload },
      });

      // Complete API call
      next(apiCallComplete({ api: "checkHolidayBooking" }));
    }

    if (action.type === `${CHECK_HOLIDAY_BOOKING} ${API_ERROR}`) {
      console.error("Error checking holiday booking status:", action.payload);

      // Set default status on error (allow booking)
      next({
        type: SET_HOLIDAY_BOOKING_STATUS,
        payload: {
          status: {
            date: action.payload?.date || "",
            location: action.payload?.location || "",
            sport: action.payload?.sport || "",
            is_blocked: false,
            booking_allowed: true,
            message: "Booking is allowed on this date",
          },
        },
      });

      // Complete API call
      next(apiCallComplete({ api: "checkHolidayBooking" }));
    }

    if (action.type === FETCH_HOLIDAY_DATES) {
      const { sport, location } = action.payload;

      // Start API call
      next(apiCallStart({ api: "fetchHolidayDates" }));

      // Make API call to fetch holiday dates
      next(
        apiAction({
          url: getHolidayDatesUrl(location, sport),
          feature: FETCH_HOLIDAY_DATES,
          method: "GET",
        })
      );
    }

    if (action.type === `${FETCH_HOLIDAY_DATES} ${API_SUCCESS}`) {
      // Set the holiday dates in the store
      next({
        type: SET_HOLIDAY_DATES,
        payload: {
          dates: action.payload.response?.dates || action.payload?.dates || [],
        },
      });

      // Complete API call
      next(apiCallComplete({ api: "fetchHolidayDates" }));
    }

    if (action.type === `${FETCH_HOLIDAY_DATES} ${API_ERROR}`) {
      console.error("Error fetching holiday dates:", action.payload);

      // Set empty dates on error
      next({
        type: SET_HOLIDAY_DATES,
        payload: { dates: [] },
      });

      // Complete API call
      next(apiCallComplete({ api: "fetchHolidayDates" }));
    }

    if (action.type === FETCH_SPECIAL_SCHEDULE_DATES) {
      const { sport, location } = action.payload;

      // Start API call
      next(apiCallStart({ api: "fetchSpecialScheduleDates" }));

      // Make API call to fetch special schedule dates
      next(
        apiAction({
          url: getSpecialScheduleDatesUrl(location, sport),
          feature: FETCH_SPECIAL_SCHEDULE_DATES,
          method: "GET",
        })
      );
    }

    if (action.type === `${FETCH_SPECIAL_SCHEDULE_DATES} ${API_SUCCESS}`) {
      // Set the special schedule dates in the store
      next({
        type: SET_SPECIAL_SCHEDULE_DATES,
        payload: {
          dates: action.payload.response?.dates || action.payload?.dates || [],
        },
      });

      // Complete API call
      next(apiCallComplete({ api: "fetchSpecialScheduleDates" }));
    }

    if (action.type === `${FETCH_SPECIAL_SCHEDULE_DATES} ${API_ERROR}`) {
      console.error("Error fetching special schedule dates:", action.payload);

      // Set empty dates on error
      next({
        type: SET_SPECIAL_SCHEDULE_DATES,
        payload: { dates: [] },
      });

      // Complete API call
      next(apiCallComplete({ api: "fetchSpecialScheduleDates" }));
    }

    if (action.type === UPDATE_SPECIAL_SCHEDULE) {
      const { sport, location, dates } = action.payload;

      // Start API call
      next(apiCallStart({ api: "updateSpecialSchedule" }));

      // Make API call to update special schedule
      next(
        apiAction({
          url: getSpecialScheduleUpdateUrl(),
          feature: UPDATE_SPECIAL_SCHEDULE,
          method: "POST",
          data: { sport, location, dates },
          meta: { sport, location },
        })
      );
    }

    if (action.type === `${UPDATE_SPECIAL_SCHEDULE} ${API_SUCCESS}`) {
      // Complete API call
      next(apiCallComplete({ api: "updateSpecialSchedule" }));

      const response = action.payload.response || action.payload;
      const added = response.added || 0;
      const deleted = response.deleted || 0;

      let message = "Special schedule updated successfully.";
      if (added > 0 && deleted > 0) {
        message = `${added} special schedule(s) added and ${deleted} removed successfully.`;
      } else if (added > 0) {
        message = `${added} special schedule(s) added successfully.`;
      } else if (deleted > 0) {
        message = `${deleted} special schedule(s) removed successfully.`;
      }

      // Show success message
      next(
        openSnackbar({
          message: message,
        })
      );

      // Refresh special schedule dates after update
      dispatch(
        fetchSpecialScheduleDates({
          sport: action.meta.sport,
          location: action.meta.location,
        })
      );
    }

    if (action.type === `${UPDATE_SPECIAL_SCHEDULE} ${API_ERROR}`) {
      console.error("Error updating special schedule:", action.payload);

      // Complete API call
      next(apiCallComplete({ api: "updateSpecialSchedule" }));
    }

    if (action.type === CHECK_DATE_PRESENCE) {
      const { date, sport, location } = action.payload;

      // Start API call
      next(apiCallStart({ api: "checkDatePresence" }));

      // Make API call to check date presence
      next(
        apiAction({
          url: getCheckDatePresenceUrl(location, sport, date),
          feature: CHECK_DATE_PRESENCE,
          method: "GET",
        })
      );
    }

    if (action.type === `${CHECK_DATE_PRESENCE} ${API_SUCCESS}`) {
      // Set the date presence status in the store
      next({
        type: SET_DATE_PRESENCE_STATUS,
        payload: { status: action.payload.response || action.payload },
      });

      // Complete API call
      next(apiCallComplete({ api: "checkDatePresence" }));
    }

    if (action.type === `${CHECK_DATE_PRESENCE} ${API_ERROR}`) {
      console.error("Error checking date presence:", action.payload);

      // Set default status on error (not present in special schedule)
      next({
        type: SET_DATE_PRESENCE_STATUS,
        payload: {
          status: {
            is_present: false,
            date: action.payload?.date || "",
          },
        },
      });

      // Complete API call
      next(apiCallComplete({ api: "checkDatePresence" }));
    }

    if (action.type === FETCH_DAY_MAPPINGS) {
      const { location } = action.payload;

      // Start API call
      next(apiCallStart({ api: "fetchDayMappings" }));

      // Make API call to fetch day mappings
      next(
        apiAction({
          url: getDayMappingsUrl(location),
          feature: FETCH_DAY_MAPPINGS,
          method: "GET",
        })
      );
    }

    if (action.type === `${FETCH_DAY_MAPPINGS} ${API_SUCCESS}`) {
      // Set the day mappings in the store
      next({
        type: SET_DAY_MAPPINGS,
        payload: {
          dayMappings:
            action.payload.response?.day_mappings ||
            action.payload?.day_mappings ||
            {},
        },
      });

      // Complete API call
      next(apiCallComplete({ api: "fetchDayMappings" }));
    }

    if (action.type === `${FETCH_DAY_MAPPINGS} ${API_ERROR}`) {
      console.error("Error fetching day mappings:", action.payload);

      // Set default day mappings on error
      next({
        type: SET_DAY_MAPPINGS,
        payload: {
          dayMappings: {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: true,
            sunday: true,
          },
        },
      });

      // Complete API call
      next(apiCallComplete({ api: "fetchDayMappings" }));
    }

    if (action.type === FETCH_PRICING_CONFIGS) {
      const { sport, location, date } = action.payload;

      // Start API call
      next(apiCallStart({ api: "fetchPricingConfigs" }));

      // Make API call to fetch pricing configs
      next(
        apiAction({
          url: getPricingConfigsUrl(sport, location, date),
          feature: FETCH_PRICING_CONFIGS,
          method: "GET",
        })
      );
    }

    if (action.type === `${FETCH_PRICING_CONFIGS} ${API_SUCCESS}`) {
      // Set the pricing configs in the store
      next({
        type: SET_PRICING_CONFIGS,
        payload: { pricingConfigs: action.payload.response || action.payload },
      });

      // Complete API call
      next(apiCallComplete({ api: "fetchPricingConfigs" }));
    }

    if (action.type === `${FETCH_PRICING_CONFIGS} ${API_ERROR}`) {
      console.error("Error fetching pricing configs:", action.payload);

      // Set default pricing configs on error
      next({
        type: SET_PRICING_CONFIGS,
        payload: {
          pricingConfigs: null,
        },
      });

      // Complete API call
      next(apiCallComplete({ api: "fetchPricingConfigs" }));
    }
  };

export default holidayBookingMiddleware;
